/* eslint-disable no-unused-vars */
import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { returnFormattedDate } from "@/lib/globalFormatterHelper";
import { returnTodayISODate } from "@/lib/businessRules/settlementDateRules";
import cpiStore, { CpiStatus } from "@/store/cpiStore";
import { fetchCpi, returnCpiKey } from "@/store/cpiStoreACTIONS";
import {
  ICpiTicket,
  returnCpiAutoFillChanges,
  returnCpiStartDate,
  returnZcsInflationIndex,
} from "./swapTicketHelper";

type ICpiAutoFillTicket = ICpiTicket & { cpiAutoKey: string; startDate: string };

// LQC-2957 (spec B9) - fills a ZCS's CPI from the backend: the current CPI, or for a
// seasoned swap the one before its start date. A CPI the user typed is never replaced.
export function useZcsCpiAutoFill<T extends ICpiAutoFillTicket>(
  ticket: T,
  update: (changes: Partial<T>) => void,
): string {
  const { getAccessTokenSilently } = useAuth0();
  const index = returnZcsInflationIndex(ticket);
  const startDate = returnCpiStartDate(ticket.startDate, returnTodayISODate());
  const key = index ? returnCpiKey(index, startDate) : "";

  useEffect(() => {
    if (key) fetchCpi(index, startDate, getAccessTokenSilently);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const result = cpiStore((state) => (key ? state.cpiByKey[key] : undefined));

  useEffect(() => {
    if (!key || result?.status !== CpiStatus.Loaded) return;
    const changes = returnCpiAutoFillChanges(ticket, key, result.cpi);
    if (Object.keys(changes).length > 0) update(changes as Partial<T>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, result, ticket.cpi, ticket.cpiAutoKey]);

  return returnCpiSourceNote(key, ticket, result?.status, result?.cpi ?? null, startDate);
}

// What the CPI Index row says about where the CPI came from.
export function returnCpiSourceNote(
  key: string,
  ticket: { cpi: number | null; cpiAutoKey: string },
  status: CpiStatus | undefined,
  cpi: number | null,
  startDate: string | undefined,
): string {
  if (!key) return "";
  if (ticket.cpi !== null && ticket.cpiAutoKey === "") return "entered";
  if (status === CpiStatus.Loading) return "looking up CPI ...";
  if (status === CpiStatus.Error) return "CPI lookup failed";
  if (status === CpiStatus.Loaded && cpi === null) return "no CPI found";
  if (ticket.cpiAutoKey === key) {
    return startDate ? `CPI before ${returnFormattedDate(startDate)}` : "current CPI";
  }
  return "";
}
